import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Plus,
  FlaskConical,
  Shield,
  User,
  Key,
  Trash2
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AdminUsersTab({
  users,
  clients,
  currentUser,
  setUserDialogOpen,
  handleRoleChange,
  handleAssignUser,
  handleToggleCalibrationAccess,
  openResetDialog,
  handleDeleteUser
}) {
  return (
    <TabsContent value="users" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">User Management</h2>
        <Button
          className="bg-primary hover:bg-primary/90"
          onClick={() => setUserDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    <FlaskConical className="w-3.5 h-3.5" />
                    Calibration
                  </div>
                </TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id} className="hover:bg-secondary/30">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center",
                        user.is_admin ? 'bg-blue-100' : 'bg-secondary'
                      )}>
                        {user.is_admin ? (
                          <Shield className="w-4 h-4 text-blue-600" />
                        ) : (
                          <User className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <Select
                      value={user.role || 'user'}
                      onValueChange={(value) => handleRoleChange(user.id, value)}
                      disabled={user.id === currentUser?.id}
                    >
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <span className="text-muted-foreground text-sm italic">All clients</span>
                    ) : (
                      <Select
                        value={user.client_id || ''}
                        onValueChange={(value) => handleAssignUser(user.id, value)}
                      >
                        <SelectTrigger className="w-40 h-8">
                          <SelectValue placeholder="Assign client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={user.calibration_access || false}
                        onCheckedChange={() => handleToggleCalibrationAccess(user.id)}
                        className="data-[state=checked]:bg-primary"
                      />
                      {user.calibration_access && (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs">
                          Active
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openResetDialog(user.id, user.name)}
                        className="text-muted-foreground hover:text-blue-600 hover:bg-blue-50"
                        title="Reset password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {!user.is_admin && user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </TabsContent>
  );
}